import React from 'react'
import { LayoutBand, Row, WhiteTheme, Separator, useContainerWidth, Expander, ExpanderContent, Cell } from '@fs/zion-ui'
import RootsTechBannerViewProvider, { viewTypes } from './internal/RootsTechBannerViewProvider'
import { useRootsTechBannerContext } from './internal/RootsTechBannerProvider'
import RootsTechBannerBackground from './internal/RootsTechBannerBackground'
import RootsTechLogo from './internal/RootsTechLogo'
import RootsTechBannerOpenCloseIcon from './internal/RootsTechBannerOpenCloseIcon'
import RootsTechBannerButton from './RootsTechBannerButton'

function splitChildrenAndButton(children, { isMobile }) {
  if (!isMobile) {
    return { contentChildren: children, bannerButton: null }
  }

  let bannerButton = null

  const contentChildren = React.Children.toArray(children).filter((child) => {
    if (!React.isValidElement(child)) {
      return true
    }

    if (!bannerButton && child.type === RootsTechBannerButton) {
      bannerButton = child
      return false
    }

    return true
  })

  return { contentChildren, bannerButton }
}

export default function RootsTechBannerExpanded({
  children,
  backgroundImage,
  backgroundSize,
  backgroundPosition,
  backgroundColor,
  mobileTopPadding = '150px',
  mobileBottomPadding = '30px',
}) {
  const atSize = useContainerWidth()
  const viewSize = atSize({ default: 'mobile', md: 'tablet', xl: 'desktop' })
  const { canToggle } = useRootsTechBannerContext()

  const layoutBandStyles = {}
  if (viewSize === 'desktop' || viewSize === 'tablet') {
    // layoutBandStyles.minHeight = '600px' //600px makes it the size of a panel. Removed to make sizing more flexible/smaller
  } else {
    layoutBandStyles.paddingTop = mobileTopPadding
    layoutBandStyles.paddingBottom = mobileBottomPadding
  }
  const isMobile = viewSize === 'mobile'

  const yMargins = <Separator size={atSize({ default: 'sm', xxl: 'xl' })} />

  const { contentChildren, bannerButton } = splitChildrenAndButton(children, { isMobile })

  return (
    <RootsTechBannerExpandedWrapper>
      <RootsTechBannerViewProvider viewType={viewTypes.EXPANDED}>
        <WhiteTheme>
          <RootsTechBannerBackground
            backgroundImage={backgroundImage}
            backgroundSize={backgroundSize}
            backgroundPosition={backgroundPosition}
            backgroundColor={backgroundColor}
          >
            {canToggle && !isMobile && (
              <LayoutBand maxWidth="xxl">
                <Separator size="md" />
                <Row alignX="end">
                  <RootsTechBannerOpenCloseIcon />
                </Row>
              </LayoutBand>
            )}
            {!canToggle && <LayoutBand maxWidth="xxl">{yMargins}</LayoutBand>}
            <LayoutBand
              maxWidth="md"
              alignY="middle"
              marginX={atSize({ default: 'sm', xs: 'xl' })}
              style={layoutBandStyles}
            >
              <Row
                alignX={atSize({ default: 'center', md: 'end', xxl: 'end' })}
                wrap={atSize({ default: false, md: true, xl: false })}
                alignY="middle"
              >
                {isMobile ? (
                  <Cell growX>
                    <div style={{ maxWidth: viewSize === 'desktop' ? '500px' : '350px' }}>
                      <RootsTechLogo height={atSize({ default: '34px', sm: '39px', md: '49px', xl: '52px' })} />
                      <Separator size={atSize({ default: 'sm', xxl: 'md' })} />
                      {contentChildren}
                    </div>
                  </Cell>
                ) : (
                  <>
                    {/* Empty spacer cell to push content to the right */}
                    <Cell />
                    <Cell growX>
                      <div style={{ maxWidth: viewSize === 'desktop' ? '500px' : '350px' }}>
                        <RootsTechLogo height={atSize({ default: '34px', sm: '39px', md: '49px', xl: '52px' })} />
                        <Separator size={atSize({ default: 'sm', xxl: 'md' })} />
                        {contentChildren}
                      </div>
                    </Cell>
                  </>
                )}
              </Row>
              {isMobile && (
                <Row alignX="start" alignY="middle" guttersX="sm">
                  <Cell alignY="middle">{bannerButton}</Cell>
                  {canToggle && (
                    <Cell noShrinkX alignY="bottom">
                      <RootsTechBannerOpenCloseIcon />
                    </Cell>
                  )}
                </Row>
              )}
            </LayoutBand>
            {yMargins}
          </RootsTechBannerBackground>
        </WhiteTheme>
      </RootsTechBannerViewProvider>
    </RootsTechBannerExpandedWrapper>
  )
}

function RootsTechBannerExpandedWrapper({ children }) {
  const { isOpen, canToggle } = useRootsTechBannerContext()
  if (!canToggle) {
    return <>{children}</>
  }
  return (
    <Expander open={isOpen} setOpen={() => {}}>
      <ExpanderContent>{children}</ExpanderContent>
    </Expander>
  )
}
